import type { NextPage } from 'next';

import { Container } from '@dansk-metal/ui';

import HeroTitle from '@web/components/hero-block/hero-title/hero-title';
import { PageCard } from '@web/components/page-card/page-card';
import { ThemedSkeleton } from '@web/components/themed-skeleton/themed-skeleton';

import TemplateLayout from '@web/layouts/template/template';

import { useAuthenticatedCRMRequestConfig, useGetUser } from '@web/services/crm-api/hooks';
import { useGetCompanyById, useGetDepartmentById, useGetPositionById } from '@web/services/crm-api/rest';
import { HeroAlignment } from '@web/services/umbraco/types/basic/Alignment';
import { BackgroundColor } from '@web/services/umbraco/types/basic/Color';
import { UmbracoPage } from '@web/services/umbraco/types/documents/UmbracoDocument';

import { MemberDtoKey, MembershipKey, PageCardItem, UserProfilePageBlock } from '@web/templates/user-profile-page/types';

import { getMembershipStatus } from '@web/utils/get-membership-type-from-flags';
import { isTruthy } from '@web/utils/guards';

import styles from './user-profile-page.module.scss';

export const userProfilePageContentAlias = 'userProfilePage' as const;


export const extendededCards = {
	contact: {
		childrenKeys: ['email', 'phoneNumber'] as MemberDtoKey[],
	},
	workplace: {
		childrenKeys: ['position', 'employer'] as MemberDtoKey[],
	},
	membership: {
		childrenKeys: ['membershipType', 'departmentName'] as MembershipKey[],
	},
} as const;

export const missingKeyMessage = {
	email: 'Du mangler en email',
	phoneNumber: 'Du mangler et telefonnummer',
	departmentName: 'Du har ingen lokalafdeling',
	employer: 'Ingen arbejdsplads',
	position: 'Ingen stilling',
	membershipType: 'Du har ikke et medlemskab',
};


export type UserProfilePageData = UmbracoPage<
	typeof userProfilePageContentAlias,
	{
		hero: {
			title: string;
			subtitle?: string;
		};
		pages: UserProfilePageBlock[];
	}
>;

const UserProfilePageTemplate: NextPage<{ pageData: UserProfilePageData }> = ({ pageData }) => {
	const { hero } = pageData.properties;
	const { requestConfig } = useAuthenticatedCRMRequestConfig();
	const { data: member, isLoaded: memberIsLoaded } = useGetUser();
	const departmentId = member?.departmentId ? member.departmentId : '';
	const skeletonCount = 9;

	const {
		data: department,
		isLoading: departmentIsLoading,
	} = useGetDepartmentById(departmentId, { query: { enabled: !!departmentId } });

	const memberCompanyId = member?.employer?.companyId ? member.employer.companyId : '';

	const {
		data: company,
		isLoading: companyIsLoading,
	} = useGetCompanyById(memberCompanyId, { query: { enabled: !!memberCompanyId }, request: requestConfig });

	const positionId = member?.positionId ? member.positionId : '';

	const {
		data: position,
		isLoading: positionIsLoading,
	} = useGetPositionById(positionId, { query: { enabled: !!positionId }, request: requestConfig });

	const extendedMember = {
		...member,
		email: member?.email,
		phoneNumber: member?.phoneNumber,
		departmentName: department?.name,
		membershipType: member?.flags && getMembershipStatus(member?.flags ? member.flags : {}),
		employer: company?.name,
		position: position?.name,
	};

	const pageLoading = (!memberIsLoaded || departmentIsLoading || companyIsLoading || positionIsLoading);

	const pages = pageData.properties.pages.map((page) => {
		const card: PageCardItem = {
			...page.contentProperties,
			key: page.key,
		};

		if (card.type && Object.keys(extendededCards).includes(card.type)) {
			card.children = extendededCards[card.type].childrenKeys.map((key) => {
				return { [key]: extendedMember[key] };
			}).filter(isTruthy);

			return card;
		}

		return card;
	});

	return (
		<TemplateLayout>
			{!!hero.title && (
				<HeroTitle
					title={hero.title}
					subtitle={hero.subtitle}
					alignment={HeroAlignment.Left}
					background={BackgroundColor.White}
				/>
			)}
			<Container className={styles.container}>
				{pageLoading ?
					(
						<ThemedSkeleton
							count={skeletonCount}
							className={styles.skeleton_theme}
							containerClassName={styles.cards}
							inline={true}
						/>
					) : (
						<div className={styles.cards}>
							<>
								{pages.map((page) => (
									<PageCard
										key={page.key}
										title={page.link?.name}
										link={page.link}
										icon={page.icon}
									>
										{page.children && (
											<div className={styles.content}>
												{page.children.map((child) => {
													return (
														<p key={Object.keys(child)[0]}>
															{Object.values(child)[0] || missingKeyMessage[Object.keys(child)[0]]}
														</p>
													);
												})}
											</div>
										)}
									</PageCard>
								))}
							</>
						</div>
					)}
			</Container>
		</TemplateLayout>
	);
};

export default UserProfilePageTemplate;
